import { usePageData } from "@/lib/pageData";
import { businessSchema } from "@/lib/business";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Building2, Calculator, HardHat, Utensils, Stethoscope, ShieldCheck, Truck, Landmark, Radio, Code2, ArrowRight, MapPin, MessageCircle } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { useSettings } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";

const ICONS = {
  "building-2": Building2, calculator: Calculator, "hard-hat": HardHat, utensils: Utensils,
  stethoscope: Stethoscope, "shield-check": ShieldCheck, truck: Truck, landmark: Landmark,
  radio: Radio, "code-2": Code2,
};

const POPULAR = [
  ["gst-registration", "GST Registration"],
  ["udyam-msme-registration", "Udyam / MSME Registration"],
  ["company-registration", "Company Registration"],
  ["shop-establishment-registration", "Shop & Establishment"],
  ["fssai-licensing", "FSSAI Licensing"],
  ["custom-website-development", "Website Development"],
];

const STEPS = [
  ["Choose a Service", "Browse the directory or search for the registration, licence or development service you need."],
  ["Discuss Requirements", "We connect on call or WhatsApp to understand your business and exact requirement."],
  ["Share Required Information", "We tell you precisely what information is needed — nothing extra, nothing sensitive online."],
  ["Receive Assistance", "We coordinate the application, filing or development work and keep you updated."],
];

const FAQS = [
  ["Which areas do you serve?", "We support business enquiries across India from our Satna, Madhya Pradesh office. Share your city and state so we can confirm availability, requirements, any local steps and charges for your case."],
  ["How much do your services cost?", "Charges depend on the service and the scope of work. Send a Request a Quote enquiry and we will share a clear quotation. Government fees, where applicable, are separate and always explained upfront."],
  ["Do you issue licences yourselves?", "No. We are a private service provider. Licences, registrations and approvals are issued by the relevant government authorities. We assist with applications, documentation and coordination."],
  ["How long does a registration or licence take?", "Timelines vary by service and by the processing time of the concerned authority. Once we understand your case, we explain the expected steps and keep you informed."],
  ["What information should I keep ready?", "After your enquiry we share a simple checklist for your specific service. Please never share Aadhaar documents, PAN scans or passwords through the website form."],
];

export default function Home() {
  const settings = useSettings();
  const initial = usePageData();
  const [categories, setCategories] = useState(initial?.categories || []);
  const [q, setQ] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const jsonLd = businessSchema(settings);

  return (
    <div data-testid="home-page">
      <SEO title="Business Registration, Compliance & Digital Services in India | Mynavodayans"
           description="Business registration, licensing, compliance, websites and apps for businesses across India. Based in Satna; confirm requirements and availability for your location."
           canonicalPath="/" jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef3fb] via-white to-white" data-testid="hero-section">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#1e2a78]/5 blur-3xl" />
        <div className="container-x relative grid items-center gap-10 py-12 sm:py-16 lg:grid-cols-2 lg:gap-14 lg:py-20">
          <div className="min-w-0">
            <div className="fade-up flex items-center gap-3" data-testid="hero-brand">
              <img src="/assets/mynavodayans-logo.png" alt="Mynavodayans Online Services logo" width={56} height={56}
                   className="h-12 w-12 object-contain sm:h-14 sm:w-14" fetchPriority="auto" loading="eager" data-testid="hero-logo" />
              <div>
                <p className="font-heading text-base font-extrabold uppercase tracking-wide text-[#1e2a78] sm:text-lg">Mynavodayans</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 sm:text-xs">Online Services Private Limited</p>
              </div>
            </div>
            <h1 className="fade-up-1 mt-6 font-heading text-3xl font-extrabold leading-[1.15] text-[#1e2a78] sm:text-4xl lg:text-[2.9rem]" data-testid="hero-headline">
              Business Registration, Compliance &amp; Digital Services Across India
            </h1>
            <p className="fade-up-2 mt-4 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="hero-subtext">
              Based in Satna, we support businesses across India with registration, licensing, compliance, websites and apps. Share your requirement and location to discuss the next steps.
            </p>
            <div className="fade-up-3 mt-7 flex flex-wrap gap-3 sm:gap-4">
              <Link to="/services" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1e2a78] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#1e2a78]/25 transition-colors duration-200 hover:bg-[#141d58]" data-testid="hero-explore-btn">
                Explore Services <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#1e2a78] px-6 py-3.5 text-sm font-bold text-[#1e2a78] transition-colors duration-200 hover:bg-[#1e2a78] hover:text-white" data-testid="hero-enquire-btn">
                Enquire Now
              </Link>
            </div>
            <form className="fade-up-3 mt-6 flex max-w-md overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm"
                  onSubmit={(e) => { e.preventDefault(); navigate(`/services?q=${encodeURIComponent(q)}`); }} data-testid="hero-search-form">
              <label htmlFor="hero-search" className="sr-only">Search services</label>
              <input id="hero-search" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search a service — GST, FSSAI, PAN, trade licence…"
                     className="w-full bg-transparent px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none" data-testid="hero-search-input" />
              <button type="submit" aria-label="Search services" className="flex items-center gap-2 bg-[#1e2a78] px-4 text-sm font-bold text-white transition-colors hover:bg-[#141d58]" data-testid="hero-search-btn">
                <Search size={16} /> <span className="hidden sm:inline">Search</span>
              </button>
            </form>
            <div className="fade-up-3 mt-6">
              <Link to="/" className="font-heading text-lg font-extrabold text-[#1e2a78] transition-colors hover:text-[#141d58] hover:underline" data-testid="hero-website-link">www.mynavodayans.in</Link>
              <p className="mt-1 text-sm text-slate-600" data-testid="hero-location">Based in Satna, Madhya Pradesh | Serving businesses across India</p>
            </div>
          </div>
          <div className="fade-up-2">
            <img src="/assets/hero-illustration.webp"
                 alt="Illustration of a laptop with an online services dashboard, verified documents, a security shield and a small storefront"
                 width={1100} height={888} fetchPriority="high" loading="eager"
                 className="h-auto w-full rounded-2xl" data-testid="hero-illustration" />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-6" data-testid="popular-services-strip">
        <div className="container-x flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <span className="font-bold text-slate-500">Popular:</span>
          {POPULAR.map(([slug, name]) => (
            <Link key={slug} to={`/services/${slug}`}
                  className="rounded-full border border-slate-200 px-3.5 py-1.5 font-medium text-slate-700 transition-colors hover:border-[#d4a017] hover:bg-amber-50"
                  data-testid={`popular-${slug}`}>{name}</Link>
          ))}
        </div>
      </section>

      <section className="container-x py-20" data-testid="categories-section">
        <p className="eyebrow-label">What we do</p>
        <h2 className="mt-2 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">Explore Our Service Categories</h2>
        <p className="mt-3 max-w-2xl text-slate-600">From company registration to website development — pick a category to see every service we assist with.</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || Building2;
            return (
              <Link key={c.slug} to={`/services/category/${c.slug}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[#d4a017]/60 hover:shadow-xl"
                    data-testid={`category-card-${c.slug}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0a192f] text-[#f5c542] transition-colors group-hover:bg-[#d4a017] group-hover:text-[#0a192f]">
                  <Icon size={22} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold leading-snug text-[#0a192f]">{c.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-500">{c.short}</p>
                <p className="mt-3 text-xs font-bold text-[#b07f0a]">{c.count} services →</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-white py-20" data-testid="process-section">
        <div className="container-x">
          <p className="eyebrow-label">Simple process</p>
          <h2 className="mt-2 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">How It Works</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-4">
            {STEPS.map(([title, text], i) => (
              <div key={title} className="relative rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid={`process-step-${i + 1}`}>
                <span className="font-heading text-4xl font-extrabold text-[#d4a017]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-[#0a192f]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x grid items-center gap-12 py-20 lg:grid-cols-2" data-testid="about-section">
        <div>
          <p className="eyebrow-label">Who we are</p>
          <h2 className="mt-2 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">A Single Point of Coordination for Your Business Needs</h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            {settings.about_short} We focus on understanding your requirement first, communicating clearly, and coordinating each step — so you spend less time chasing paperwork and more time running your business.
          </p>
          <p className="mt-4 leading-relaxed text-slate-600">
            Mynavodayans is a private service provider. We do not issue licences or approvals ourselves — those are granted by the relevant government authorities. Our role is application, documentation and coordination assistance.
          </p>
          <Link to="/about" className="btn-navy mt-6" data-testid="about-read-more">More About Us <ArrowRight size={16} /></Link>
        </div>
        <div className="rounded-3xl bg-[#0a192f] p-8 text-white lg:p-10">
          <div className="flex items-center gap-3">
            <MapPin className="text-[#f5c542]" size={22} />
            <h3 className="font-heading text-xl font-bold">Based in Satna, Serving Businesses Across India</h3>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/75">
            We welcome enquiries from businesses across India. Our team coordinates from Satna over call and WhatsApp, with the scope, applicable requirements and any local or in-person steps confirmed for your state or union territory.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["28 states", "8 union territories", "Office in Satna", "Availability confirmed"].map((c) => (
              <span key={c} className="rounded-full border border-white/15 px-3 py-1 text-xs font-medium text-white/80">{c}</span>
            ))}
          </div>
          <Link to="/service-areas" className="mt-6 flex w-fit items-center gap-2 text-sm font-bold text-[#f5c542] hover:underline" data-testid="india-section-link">
            Service Areas — India <ArrowRight size={15} />
          </Link>
          <Link to="/madhya-pradesh" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white/75 hover:underline" data-testid="mp-section-link">
            Madhya Pradesh regional details <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="faq-section">
        <div className="container-x max-w-3xl">
          <p className="eyebrow-label">Good to know</p>
          <h2 className="mt-2 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">Frequently Asked Questions</h2>
          <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-slate-50">
            {FAQS.map(([question, answer], i) => (
              <div key={question}>
                <button className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-heading text-base font-bold text-[#0a192f]"
                        onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i} aria-controls={"home-faq-answer-" + i} data-testid={`faq-toggle-${i}`}>
                  {question}
                  <span className={`text-[#d4a017] transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
                </button>
                <p id={"home-faq-answer-" + i} hidden={openFaq !== i} className="px-6 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20" data-testid="home-enquiry-section">
        <div className="grid gap-10 rounded-3xl bg-[#0a192f] p-8 lg:grid-cols-2 lg:p-14">
          <div className="text-white">
            <p className="eyebrow-label !text-[#f5c542]">Get started</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">Tell Us What You Need</h2>
            <p className="mt-4 text-white/70">Share a few details and our team will call or message you back with next steps and a clear quotation.</p>
            <p className="mt-8 text-xs font-bold uppercase tracking-wider text-white/50">Prefer to talk? Call or WhatsApp us directly</p>
            <a href={settings.phone_link} className="mt-2 inline-flex items-center gap-3 rounded-xl bg-white/5 px-5 py-4 font-heading text-xl font-extrabold text-[#f5c542]" data-testid="enquiry-phone-display">
              {settings.phone_display}
            </a>
            <p className="mt-2 text-xs text-white/50">Available on Call & WhatsApp</p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-2xl lg:p-8">
            <EnquiryForm sourcePage="/" />
          </div>
        </div>
      </section>
    </div>
  );
}
